import React from "react";

import storyImageOne from "./images/graduations/graduation_15.jpg";
import storyImageTwo from "./images/headshots/headshot_01.jpg";
import storyImageThree from "./images/portraits/02_portaits.jpg";

const FLOATING_HEARTS = [
  {
    symbol: "♥",
    left: "4%",
    delay: "0s",
    duration: "6.8s",
    size: "18px",
    drift: "-12px",
    rotate: "-12deg",
  },
  {
    symbol: "♡",
    left: "23%",
    delay: "1.3s",
    duration: "7.8s",
    size: "25px",
    drift: "13px",
    rotate: "10deg",
  },
  {
    symbol: "♥",
    left: "46%",
    delay: "3.1s",
    duration: "7.1s",
    size: "15px",
    drift: "-8px",
    rotate: "-8deg",
  },
  {
    symbol: "✦",
    left: "65%",
    delay: "0.7s",
    duration: "8.4s",
    size: "15px",
    drift: "10px",
    rotate: "15deg",
  },
  {
    symbol: "♥",
    left: "85%",
    delay: "2.3s",
    duration: "7.4s",
    size: "20px",
    drift: "-14px",
    rotate: "12deg",
  },
];

function FloatingHearts({ id }) {
  return (
    <div
      className="pointer-events-none absolute inset-x-0 -top-4 h-32 overflow-visible"
      aria-hidden="true"
    >
      {FLOATING_HEARTS.map((heart, index) => (
        <span
          key={`${id}-${heart.symbol}-${index}`}
          className="floating-heart absolute bottom-0 text-rose-500"
          style={{
            left: heart.left,
            fontSize: heart.size,
            "--heart-drift": heart.drift,
            "--heart-rotate": heart.rotate,
            animation: `heart-float-loop ${heart.duration} cubic-bezier(0.16, 1, 0.3, 1) ${heart.delay} infinite`,
          }}
        >
          {heart.symbol}
        </span>
      ))}
    </div>
  );
}

function StoryHeading() {
  return (
    <>
      <p className="mb-6 text-xs font-medium uppercase tracking-[0.3em] text-stone-500">
        Personal story
      </p>

      <div className="relative inline-block">
        <h2 className="select-none font-serif text-[clamp(3.1rem,13vw,5.8rem)] leading-[0.9] tracking-[-0.055em] text-stone-900 lg:text-[clamp(3.1rem,6vw,5.8rem)]">
          Images
          <span className="mt-2 block pl-[0.1em] italic text-stone-500">
            that feel like you.
          </span>
        </h2>

        <FloatingHearts id="story-heading" />
      </div>
    </>
  );
}

function StoryCopy() {
  return (
    <>
      <div className="my-10 h-px w-20 bg-stone-400" />

      <div className="max-w-xl space-y-6 text-lg leading-8 text-stone-700 sm:text-xl sm:leading-9">
        <p>
          I’m Alejandro Armas, an experienced portrait photographer with more
          than 13 years of experience based in Los Angeles. I create images for
          graduates, creatives, families, and people who want to be
          photographed with presence and ease.
        </p>

        <p>
          Every session begins simply: thoughtful direction, natural light, and
          time to settle in. The photographs are polished but never
          overworked—made to feel clear, personal, and lasting.
        </p>
      </div>

      <a
        href="/about"
        className="group mt-10 inline-flex items-center gap-4 border-b border-stone-900 pb-2 text-xs font-medium uppercase tracking-[0.2em] text-stone-900 transition-colors duration-300 hover:border-stone-500 hover:text-stone-500 focus:outline-none focus:ring-2 focus:ring-stone-500 focus:ring-offset-4"
      >
        More about my approach
        <span className="text-lg leading-none transition-transform duration-300 group-hover:translate-x-1 motion-reduce:transition-none">
          →
        </span>
      </a>
    </>
  );
}

function ArtImage({ src, alt, aspectClass }) {
  return (
    <figure className="pointer-events-none w-full bg-white p-2 shadow-[0_18px_50px_rgba(28,25,23,0.1)] sm:p-3">
      <div
        className={`flex ${aspectClass} items-center justify-center bg-stone-100`}
      >
        <img
          src={src}
          alt={alt}
          className="h-full w-full select-none object-contain"
          loading="lazy"
          draggable="false"
        />
      </div>
    </figure>
  );
}

function PersonalStory() {
  return (
    <section className="relative isolate overflow-hidden bg-[#f7f6f2] px-5 py-20 sm:px-6 sm:py-28 md:px-8 lg:px-10 lg:py-32 xl:px-12">
      <style>{`
        @keyframes heart-float-loop {
          0% {
            opacity: 0;
            transform: translate3d(0, 12px, 0) scale(0.55) rotate(var(--heart-rotate));
          }

          12% {
            opacity: 0.78;
          }

          62% {
            opacity: 0.54;
          }

          100% {
            opacity: 0;
            transform: translate3d(var(--heart-drift), -96px, 0) scale(1.08) rotate(var(--heart-rotate));
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .floating-heart {
            display: none;
          }
        }
      `}</style>

      <div className="mx-auto max-w-[1600px]">
        {/* Mobile and tablet layout */}
     <div className="lg:hidden">
  <div className="mx-auto w-full max-w-2xl">
    {/* Hero image */}
    <ArtImage
      src={storyImageOne}
      alt="Portrait photography by Alejandro Armas"
      aspectClass="aspect-[4/5]"
      position="object-center"
    />

    {/* Smaller editorial pair */}
    <div className="mt-5 grid grid-cols-5 items-end gap-3 sm:mt-8 sm:gap-5">
      <div className="col-span-3">
        <ArtImage
          src={storyImageTwo}
          alt="Natural-light photography by West LA Portraits"
          aspectClass="aspect-[3/4]"
          position="object-center"
        />
      </div>

      <div className="col-span-2 pb-8 sm:pb-12">
        <ArtImage
          src={storyImageThree}
          alt="Los Angeles portrait session by Alejandro Armas"
          aspectClass="aspect-square"
          position="object-center"
        />
      </div>
    </div>

    <p className="mt-6 text-xs font-medium uppercase tracking-[0.24em] text-stone-500">
      Los Angeles · California
    </p>
  </div>

  <div className="mx-auto mt-14 max-w-2xl">
    <StoryHeading />
    <StoryCopy />
  </div>
</div>

        {/* Desktop layout */}
        <div className="hidden lg:grid lg:grid-cols-[1.1fr_0.9fr] lg:items-start lg:gap-20 xl:gap-28">
          {/* Left: Fibonacci 8:5 image relationship */}
          <div className="pointer-events-none mx-auto w-full max-w-[980px]">
            <div className="grid grid-cols-8 gap-8">
              <div className="col-span-8">
                <ArtImage
                  src={storyImageOne}
                  alt="Portrait photography by Alejandro Armas"
                  aspectClass="aspect-[3/4]"
                />
              </div>

              <div className="col-span-5">
                <ArtImage
                  src={storyImageTwo}
                  alt="Natural-light photography by West LA Portraits"
                  aspectClass="aspect-[3/4]"
                />
              </div>

              <div className="col-span-3" aria-hidden="true" />
            </div>

            <p className="mt-8 text-xs font-medium uppercase tracking-[0.24em] text-stone-500">
              Los Angeles · California
            </p>
          </div>

          {/* Right: image three sits above text */}
          <div className="mx-auto w-full max-w-2xl lg:pl-4">
            <ArtImage
              src={storyImageThree}
              alt="Los Angeles portrait session by Alejandro Armas"
              aspectClass="aspect-[4/3]"
            />

            <div className="mt-12">
              <StoryHeading />
              <StoryCopy />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default PersonalStory;